import { sharedSearchCache } from "../SearchCache";
import { APIResponse } from '../APITypes';
import { csrfHeaders } from '../csrf';

export type SearchOptions = {
    parameters?: Record<string, any>;
    sortColumn?: string;
    sortDirection?: "asc" | "desc";
};

export type SearchResult<T> = {
    total: number;
    results: T[];
};

export default class SearchEndpoint<T> {
    private pageSize: number;
    private fetchWindow: number;
    private apiEndPoint: string;
    private abortController?: AbortController;

    constructor(apiEndPoint: string, pageSize = 15, fetchWindow = 3) {
        this.apiEndPoint = apiEndPoint || '/api/search';
        this.pageSize = pageSize;
        this.fetchWindow = fetchWindow;
    }

    private getCacheSlice(from: number, to: number): T[] {
        // Ensure cache is an array before slicing
        if (!Array.isArray(sharedSearchCache.cache)) {
            return [];
        }
        const start = from - sharedSearchCache.cachePosition;
        const end = to - sharedSearchCache.cachePosition;
        return sharedSearchCache.cache.slice(start, end) as T[];
    }

    /**
     * Search with paging arguments and search options.
     * @param from - Start index (inclusive)
     * @param to - End index (exclusive)
     * @param options - SearchOptions (sorting/filtering only)
     */
    async search(from: number, to: number, options: SearchOptions): Promise<APIResponse<SearchResult<T>>> {
        const { parameters, sortColumn, sortDirection } = options;
        const cacheParams = { parameters, sortColumn, sortDirection };

        if (
            SearchEndpoint.deepEqual(cacheParams, sharedSearchCache.lastParameters) &&
            this.apiEndPoint === sharedSearchCache.lastApiEndPoint &&
            from >= sharedSearchCache.cachePosition &&
            to <= sharedSearchCache.cachePosition + sharedSearchCache.cacheSize
        ) {
            // Trigger prefetch if near cache end
            if (to >= sharedSearchCache.cachePosition + sharedSearchCache.cacheSize - this.pageSize && !sharedSearchCache.isPrefetching) {
                sharedSearchCache.prefetchPromise = this.prefetchNextPage(options);
            }
            return {
                errors: [],
                data: {
                    total: sharedSearchCache.total,
                    results: this.getCacheSlice(from, to)
                }
            };
        }

        // If a prefetch is running and will provide the needed data, wait for it
        if (
            sharedSearchCache.isPrefetching &&
            sharedSearchCache.prefetchFrom !== undefined &&
            sharedSearchCache.prefetchTo !== undefined &&
            from >= sharedSearchCache.prefetchFrom &&
            to <= sharedSearchCache.prefetchTo
        ) {
            // Only wait for prefetch if parameters match
            if (SearchEndpoint.deepEqual(cacheParams, sharedSearchCache.lastParameters) && this.apiEndPoint === sharedSearchCache.lastApiEndPoint) {
                await sharedSearchCache.prefetchPromise;
                if (
                    from >= sharedSearchCache.cachePosition &&
                    to <= sharedSearchCache.cachePosition + sharedSearchCache.cacheSize
                ) {
                    return {
                        errors: [],
                        data: {
                            total: sharedSearchCache.total,
                            results: this.getCacheSlice(from, to)
                        }
                    };
                }
                // TODO: put logging right here, should never get here, i think......
            }
            // If parameters changed, skip waiting and fetch new data
        }

        // Otherwise, fetch and replace cache
        const windowSize = this.fetchWindow * this.pageSize;
        const expandedFrom = Math.max(0, from - windowSize);
        const expandedTo = to + windowSize;
        // Ensure consistent window size at edges and middle
        // expandedTo - expandedFrom should always be (2 * windowSize + pageSize)
        const results = await this.fetchRange(expandedFrom, expandedTo, options, 'replace', from, to);
        return {
            errors: [],
            data: {
                total: sharedSearchCache.total,
                results
            }
        };
    }

    /**
     * Paged search with explicit paging and search options.
     * @param page - Page number (1-based)
     * @param options - SearchOptions (sorting/filtering only)
     */
    async pagedSearch(page: number, options: SearchOptions): Promise<APIResponse<SearchResult<T>>> {
        // Pages start at 1, rows start at 0
        const from = (page - 1) * this.pageSize;
        const to = from + this.pageSize;
        try {
            const result = await this.search(from, to, options);
            if (!result || typeof result !== "object" || !Array.isArray(result.data?.results)) {
                return { errors: [{ message: 'Invalid response' }], data: { total: 0, results: [] } };
            }
            return result;
        } catch (err: any) {
            return { errors: [{ message: err?.message || 'Search error' }], data: { total: 0, results: [] } };
        }
    }

    /**
     * Prefetch next page using search options (sorting/filtering only).
     * @param options - SearchOptions
     */
    async prefetchNextPage(options: SearchOptions): Promise<void> {
        const windowSize = this.fetchWindow * this.pageSize;
        // Prefetch a consistent window size
        sharedSearchCache.prefetchFrom = sharedSearchCache.cachePosition + sharedSearchCache.cacheSize;
        sharedSearchCache.prefetchTo = sharedSearchCache.prefetchFrom + windowSize;
        sharedSearchCache.isPrefetching = true;
        await this.fetchRange(sharedSearchCache.prefetchFrom, sharedSearchCache.prefetchTo, options, 'append');
        sharedSearchCache.isPrefetching = false;
    }

    /**
     * Fetch a range of results with paging and search options.
     * @param from - Start index (inclusive)
     * @param to - End index (exclusive)
     * @param options - SearchOptions (sorting/filtering only)
     * @param mode - 'replace' or 'append'
     * @param sliceFrom - Optional slice start
     * @param sliceTo - Optional slice end
     */
    private async fetchRange(
        from: number,
        to: number,
        options: SearchOptions,
        mode: 'replace' | 'append' = 'replace',
        sliceFrom?: number,
        sliceTo?: number
    ): Promise<T[]> {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const { parameters, sortColumn, sortDirection } = options;
        try {
            const response = await fetch(this.apiEndPoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
                body: JSON.stringify({ from, to, parameters, sortColumn, sortDirection }),
                signal: this.abortController.signal
            });
            if (!response.ok) throw new Error(`Search failed: HTTP ${response.status}`);
            const responseData = await response.json();

            // Server always returns APIResponse<SearchResult<T>>
            const apiResp = responseData as APIResponse<SearchResult<T>>;

            // Check for API-level errors (e.g. auth failures, validation)
            if (apiResp?.errors && apiResp.errors.length > 0) {
                const errorMsg = apiResp.errors.map((e: any) => e.message || 'Unknown error').join('; ');
                throw new Error(errorMsg);
            }
            
            // Extract data from the APIResponse structure
            const searchResult = apiResp?.data;
            const data = searchResult?.results ?? [];
            // Only use received total if it's a valid positive number, otherwise keep cached total
            const receivedTotal = searchResult?.total;
            const total = (receivedTotal != null && receivedTotal > 0) ? receivedTotal : sharedSearchCache.total;

            if (mode === 'replace') {
                // Ensure data is always an array
                const cacheData = Array.isArray(data) ? data : [];
                sharedSearchCache.cache = cacheData;
                sharedSearchCache.cachePosition = from;
                sharedSearchCache.cacheSize = cacheData.length;
                sharedSearchCache.total = total;
                sharedSearchCache.lastParameters = { parameters, sortColumn, sortDirection };
                sharedSearchCache.lastApiEndPoint = this.apiEndPoint;
                if (sliceFrom !== undefined && sliceTo !== undefined) {
                    return this.getCacheSlice(sliceFrom, sliceTo);
                }
                return cacheData as T[];
            } else if (mode === 'append') {
                if (from === sharedSearchCache.cachePosition + sharedSearchCache.cacheSize) {
                    // Ensure both cache and data are arrays before concat
                    const currentCache = Array.isArray(sharedSearchCache.cache) ? sharedSearchCache.cache : [];
                    const newData = Array.isArray(data) ? data : [];
                    sharedSearchCache.cache = currentCache.concat(newData);
                    sharedSearchCache.cacheSize += newData.length;
                    if (total !== undefined) {
                        sharedSearchCache.total = total;
                    }
                }
                return [];
            }
            return [];
        } catch (err: any) {
            if (err instanceof DOMException && err.name === "AbortError") {
                // Ignore aborted fetch
                return [];
            }
            throw err;
        }
    }

    /**
     * Search for CSV export - bypasses cache completely.
     * Fetches all records (or up to maxRecords) in a single request.
     * @param options - SearchOptions (sorting/filtering)
     * @param maxRecords - Maximum number of records to fetch (default: 100000)
     */
    async searchNoCacheForCsv(options: SearchOptions, maxRecords = 100000): Promise<APIResponse<SearchResult<T>>> {
        const { parameters, sortColumn, sortDirection } = options;
        
        try {
            const response = await fetch(this.apiEndPoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
                body: JSON.stringify({ 
                    from: 0, 
                    to: maxRecords,  
                    parameters,
                    sortColumn, 
                    sortDirection 
                })
            });
            
            if (!response.ok) {
                throw new Error(`CSV search failed: ${response.status} ${response.statusText}`);
            }
            
            const responseData = await response.json();
            const apiResp = responseData as APIResponse<SearchResult<T>>;
            
            return apiResp;
        } catch (err: any) {
            return { 
                errors: [{ message: err?.message || 'CSV search failed' }], 
                data: { total: 0, results: [] } 
            };
        }
    }

    static deepEqual(a: any, b: any): boolean {
        if (a === b) return true;
        if (typeof a !== typeof b) return false;
        if (typeof a !== "object" || a === null || b === null) return false;
        if (Array.isArray(a) && Array.isArray(b)) {
            if (a.length !== b.length) return false;
            for (let i = 0; i < a.length; i++) {
                if (!SearchEndpoint.deepEqual(a[i], b[i])) return false;
            }
            return true;
        }
        const aKeys = Object.keys(a);
        const bKeys = Object.keys(b);
        if (aKeys.length !== bKeys.length) return false;
        for (let key of aKeys) {
            if (!bKeys.includes(key)) return false;
            if (!SearchEndpoint.deepEqual(a[key], b[key])) return false;
        }
        return true;
    }
}
